import { arrow, offset, shift, useFloating } from '@floating-ui/react-dom';
import { Icon } from '@ps/isp-web-component-library';
import { ChevronMediumDown } from '@ps/isp-web-component-library/icons/ChevronMediumDown';
import { TickMedium } from '@ps/isp-web-component-library/icons/TickMedium';
import type { ClassValue } from 'clsx';
import classnames from 'clsx';
import { useSelect } from 'downshift';
import type { LegacyRef } from 'react';
import React, { useRef } from 'react';

import useLayoutEffect from '@swf/core/hooks/useIsomorphicLayoutEffect';

import styles from './Select.scss';

export interface SelectItem<T> {
  name: string;
  value: T;
}

export interface Props<T> {
  className?: ClassValue;
  initialSelectedItem?: SelectItem<T>;
  items: SelectItem<T>[];
  label: string;
  disabled?: boolean;
  onChange?: (item: SelectItem<T>) => void;
}

export const Select = <T,>({
  className,
  items,
  initialSelectedItem = items[0],
  label,
  disabled = false,
  onChange
}: React.PropsWithChildren<Props<T>>) => {
  const arrowRef = useRef(null);

  const { x, y, refs, strategy, update, middlewareData } = useFloating({
    placement: 'bottom',
    middleware: [offset(10), shift({ padding: 16 }), arrow({ element: arrowRef })]
  });

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect({
    items,
    initialSelectedItem,
    itemToString: (item) => item?.name || '',
    onStateChange: (changes) => {
      if (changes.selectedItem && onChange) {
        onChange(changes.selectedItem);
      }
    }
  });

  useLayoutEffect(() => {
    update();
  }, [isOpen, update]);

  return (
    <div className={classnames(className, styles.select)}>
      <label {...getLabelProps()} className={styles.label}>
        {label}
      </label>

      <button
        type="button"
        className={classnames(styles.button, { [styles.buttonActive]: isOpen })}
        {...getToggleButtonProps({
          ref: refs.reference as LegacyRef<HTMLButtonElement>,
          disabled
        })}
      >
        <span className={styles.selectedText}>{selectedItem?.name}</span>

        <Icon
          icon={ChevronMediumDown}
          className={classnames(styles.chevron, { [styles.chevronOpen]: isOpen })}
          size="x1"
        />
      </button>

      <div
        className={classnames({ [styles.menu]: isOpen })}
        {...getMenuProps({ ref: refs.floating })}
        style={{ position: strategy, top: y, left: x }}
      >
        <ul>
          {isOpen &&
            items.map((item, index) => (
              <li
                key={`${item}${index}`}
                {...getItemProps({ item, index })}
                className={classnames(styles.option, {
                  [styles.optionActive]: highlightedIndex === index
                })}
              >
                {item.name}
                {selectedItem?.name === item.name && (
                  <Icon icon={TickMedium} className={styles.selectedIcon} size="x1" />
                )}
              </li>
            ))}
        </ul>

        <div
          ref={arrowRef}
          className={classnames({
            [styles.arrow]: isOpen,
            [styles.firstItemActive]: highlightedIndex === 0
          })}
          style={{
            left: middlewareData.arrow?.x ?? ''
          }}
        />
      </div>
    </div>
  );
};

export default Select;
